import React, { useState, useEffect } from 'react';
import { Link, useNavigate, useLocation } from 'react-router-dom';
import { useForm } from 'react-hook-form';
import styled from 'styled-components';
import { motion } from 'framer-motion';
import { Eye, EyeOff, Mail, Lock, LogIn, ArrowLeft, User as UserIcon, Image as ImageIcon } from 'lucide-react';
import { useAuth } from '../contexts/AuthContext';
import toast from 'react-hot-toast';

const LoginContainer = styled.div`
  min-height: 100vh;
  display: flex;
  align-items: center;
  justify-content: center;
  background: var(--gradient-primary);
  padding: var(--spacing-lg);
`;

const LoginCard = styled(motion.div)`
  background: var(--color-background);
  border-radius: var(--border-radius-xl);
  box-shadow: 0 20px 40px rgba(0, 0, 0, 0.1);
  padding: var(--spacing-2xl);
  width: 100%;
  max-width: 400px;
  position: relative;
  overflow: hidden;
  
  &::before {
    content: '';
    position: absolute;
    top: 0;
    left: 0;
    right: 0;
    height: 4px;
    background: var(--gradient-primary);
  }
`;

const BackButton = styled(Link)`
  display: inline-flex;
  align-items: center;
  gap: var(--spacing-sm);
  color: var(--color-text-muted);
  text-decoration: none;
  font-size: 0.875rem;
  margin-bottom: var(--spacing-lg);
  transition: color var(--animation-duration) ease;
  
  &:hover {
    color: var(--color-primary);
    text-decoration: none;
  }
  
  svg {
    width: 16px;
    height: 16px;
  }
`;

const Title = styled.h1`
  font-size: 2rem;
  font-weight: 700;
  color: var(--color-text-primary);
  margin-bottom: var(--spacing-sm);
  text-align: center;
`;

const Subtitle = styled.p`
  color: var(--color-text-secondary);
  text-align: center;
  margin-bottom: var(--spacing-2xl);
  line-height: 1.5;
`;

const Form = styled.form`
  display: flex;
  flex-direction: column;
  gap: var(--spacing-lg);
`;

const FormGroup = styled.div`
  position: relative;
`;

const Label = styled.label`
  display: block;
  font-weight: 500;
  color: var(--color-text-primary);
  margin-bottom: var(--spacing-sm);
  font-size: 0.875rem;
`;

const InputWrapper = styled.div`
  position: relative;
  
  .icon {
    position: absolute;
    left: var(--spacing-md);
    top: 50%;
    transform: translateY(-50%);
    color: var(--color-text-muted);
    width: 18px;
    height: 18px;
  }
  
  .toggle-icon {
    position: absolute;
    right: var(--spacing-md);
    top: 50%;
    transform: translateY(-50%);
    color: var(--color-text-muted);
    cursor: pointer;
    width: 18px;
    height: 18px;
    transition: color var(--animation-duration) ease;
    
    &:hover {
      color: var(--color-primary);
    }
  }
`;

const Input = styled.input`
  width: 100%;
  padding: var(--spacing-md) var(--spacing-md) var(--spacing-md) 48px;
  border: 2px solid var(--color-border);
  border-radius: var(--border-radius-md);
  font-size: 1rem;
  transition: all var(--animation-duration) ease;
  background: var(--color-background);
  color: var(--color-text-primary);
  
  &:focus {
    outline: none;
    border-color: var(--color-primary);
    box-shadow: 0 0 0 3px rgba(102, 126, 234, 0.1);
  }
  
  &.error {
    border-color: #ef4444;
  }
  
  &::placeholder {
    color: var(--color-text-muted);
  }
`;

const ErrorMessage = styled.span`
  color: #ef4444;
  font-size: 0.875rem;
  margin-top: var(--spacing-xs);
  display: block;
`;

const SubmitButton = styled.button`
  width: 100%;
  padding: var(--spacing-md);
  background: var(--gradient-primary);
  color: white;
  border: none;
  border-radius: var(--border-radius-md);
  font-size: 1rem;
  font-weight: 600;
  cursor: pointer;
  transition: all var(--animation-duration) ease;
  display: flex;
  align-items: center;
  justify-content: center;
  gap: var(--spacing-sm);
  min-height: 48px;
  
  &:hover:not(:disabled) {
    transform: translateY(-2px);
    box-shadow: 0 8px 25px rgba(102, 126, 234, 0.4);
  }
  
  &:disabled {
    opacity: 0.6;
    cursor: not-allowed;
    transform: none;
  }
  
  svg {
    width: 18px;
    height: 18px;
  }
`;

const LoadingSpinner = styled.div`
  width: 18px;
  height: 18px;
  border: 2px solid rgba(255, 255, 255, 0.3);
  border-top: 2px solid white;
  border-radius: 50%;
  animation: spin 1s linear infinite;
  
  @keyframes spin {
    0% { transform: rotate(0deg); }
    100% { transform: rotate(360deg); }
  }
`;

const Divider = styled.div`
  display: flex;
  align-items: center;
  margin: var(--spacing-lg) 0;
  
  &::before,
  &::after {
    content: '';
    flex: 1;
    height: 1px;
    background: var(--color-border);
  }
  
  span {
    padding: 0 var(--spacing-md);
    color: var(--color-text-muted);
    font-size: 0.875rem;
  }
`;

const SignupLink = styled.div`
  text-align: center;
  color: var(--color-text-secondary);
  
  a {
    color: var(--color-primary);
    text-decoration: none;
    font-weight: 500;
    
    &:hover {
      text-decoration: underline;
    }
  }
`;

const ForgotPassword = styled(Link)`
  color: var(--color-primary);
  text-decoration: none;
  font-size: 0.875rem;
  text-align: center;
  display: block;
  margin-top: var(--spacing-md);
  
  &:hover {
    text-decoration: underline;
  }
`;

const GuestSection = styled.div`
  margin-top: var(--spacing-xl);
  border-top: 1px solid var(--color-border);
  padding-top: var(--spacing-xl);
`;

const GuestTitle = styled.h2`
  font-size: 1.25rem;
  font-weight: 700;
  color: var(--color-text-primary);
  margin-bottom: var(--spacing-md);
  display: flex;
  align-items: center;
  gap: var(--spacing-sm);
`;

const AvatarGrid = styled.div`
  display: grid;
  grid-template-columns: repeat(5, 1fr);
  gap: var(--spacing-md);
  margin-top: var(--spacing-sm);
`;

const AvatarItem = styled.button`
  width: 56px;
  height: 56px;
  border-radius: 50%;
  overflow: hidden;
  border: 2px solid ${props => (props.selected ? 'var(--color-primary)' : 'var(--color-border)')};
  background: var(--color-surface);
  display: flex;
  align-items: center;
  justify-content: center;
  cursor: pointer;
  transition: all var(--animation-duration) ease;

  &:hover {
    transform: translateY(-2px);
    box-shadow: 0 8px 25px rgba(102, 126, 234, 0.2);
  }

  img {
    width: 100%;
    height: 100%;
    object-fit: cover;
  }

  .emoji {
    font-size: 28px;
  }
`;

const Login = () => {
  const { login, guestLogin, loading, isAuthenticated } = useAuth();
  const navigate = useNavigate();
  const location = useLocation();
  const [showPassword, setShowPassword] = useState(false);
  const [guestNickname, setGuestNickname] = useState('');
  const [guestAvatar, setGuestAvatar] = useState('');
  const avatarOptions = [
    '/avatars/panda.svg', '/avatars/dragon.svg', '/avatars/lion.svg', '/avatars/unicorn.svg', '/avatars/tiger.svg',
    'emoji:🐼', 'emoji:🐲', 'emoji:🦁', 'emoji:🦄', 'emoji:🐯'
  ];
  
  const {
    register,
    handleSubmit,
    formState: { errors, isSubmitting },
    setError
  } = useForm();

  // 如果已经登录，重定向到首页或返回页面
  useEffect(() => {
    if (isAuthenticated) {
      const returnUrl = new URLSearchParams(location.search).get('returnUrl');
      navigate(returnUrl || '/', { replace: true });
    }
  }, [isAuthenticated, navigate, location]);

  const onSubmit = async (data) => {
    try {
      const result = await login(data.email, data.password);
      
      if (result.success) {
        const returnUrl = new URLSearchParams(location.search).get('returnUrl');
        navigate(returnUrl || '/', { replace: true });
      } else {
        setError('root', {
          type: 'manual',
          message: result.error || '登录失败'
        });
      }
    } catch (error) {
      console.error('Login error:', error);
      setError('root', {
        type: 'manual',
        message: '登录过程中发生错误'
      });
    }
  };

  const togglePasswordVisibility = () => {
    setShowPassword(!showPassword);
  };

  const handleGuestLogin = async () => {
    if (!guestNickname || guestNickname.trim().length < 2) {
      toast.error('请输入至少2个字符的昵称');
      return;
    }
    const result = await guestLogin(guestNickname.trim(), guestAvatar || undefined);
    if (result.success) {
      const returnUrl = new URLSearchParams(location.search).get('returnUrl');
      navigate(returnUrl || '/', { replace: true });
    }
  };

  return (
    <LoginContainer>
      <LoginCard
        initial={{ opacity: 0, y: 20 }}
        animate={{ opacity: 1, y: 0 }}
        transition={{ duration: 0.5 }}
      >
        <BackButton to="/">
          <ArrowLeft />
          返回首页
        </BackButton>
        
        <Title>欢迎回来</Title>
        <Subtitle>登录您的账户，继续探索中华历史文明</Subtitle>
        
        <Form onSubmit={handleSubmit(onSubmit)}>
          <FormGroup>
            <Label htmlFor="email">邮箱地址</Label>
            <InputWrapper>
              <Mail className="icon" />
              <Input
                id="email"
                type="email"
                placeholder="请输入您的邮箱地址"
                className={errors.email ? 'error' : ''}
                {...register('email', {
                  required: '请输入邮箱地址',
                  pattern: {
                    value: /^[A-Z0-9._%+-]+@[A-Z0-9.-]+\.[A-Z]{2,}$/i,
                    message: '请输入有效的邮箱地址'
                  }
                })}
              />
            </InputWrapper>
            {errors.email && (
              <ErrorMessage>{errors.email.message}</ErrorMessage>
            )}
          </FormGroup>
          
          <FormGroup>
            <Label htmlFor="password">密码</Label>
            <InputWrapper>
              <Lock className="icon" />
              <Input
                id="password"
                type={showPassword ? 'text' : 'password'}
                placeholder="请输入您的密码"
                className={errors.password ? 'error' : ''}
                {...register('password', {
                  required: '请输入密码',
                  minLength: {
                    value: 6,
                    message: '密码长度至少6个字符'
                  }
                })}
              />
              {showPassword ? (
                <EyeOff className="toggle-icon" onClick={togglePasswordVisibility} />
              ) : (
                <Eye className="toggle-icon" onClick={togglePasswordVisibility} />
              )}
            </InputWrapper>
            {errors.password && (
              <ErrorMessage>{errors.password.message}</ErrorMessage>
            )}
          </FormGroup>
          
          {errors.root && (
            <ErrorMessage>{errors.root.message}</ErrorMessage>
          )}
          
          <SubmitButton type="submit" disabled={isSubmitting || loading}>
            {isSubmitting || loading ? (
              <LoadingSpinner />
            ) : (
              <>
                <LogIn />
                登录
              </>
            )}
          </SubmitButton>
          
          <ForgotPassword to="/forgot-password">
            忘记密码？
          </ForgotPassword>
        </Form>
        
        <Divider>
          <span>还没有账户？</span>
        </Divider>

        <SignupLink>
          <Link to={`/register${location.search}`}>立即注册</Link>
        </SignupLink>

        <GuestSection>
          <GuestTitle>
            <UserIcon />
            游客登录
          </GuestTitle>
          <FormGroup>
            <Label htmlFor="guestNickname">昵称</Label>
            <InputWrapper>
              <UserIcon className="icon" />
              <Input
                id="guestNickname"
                type="text"
                placeholder="输入游客昵称（2-20字符）"
                value={guestNickname}
                onChange={(e) => setGuestNickname(e.target.value)}
              />
            </InputWrapper>
          </FormGroup>

          <Label>选择头像（可选）</Label>
          <AvatarGrid>
            {avatarOptions.map((opt, idx) => (
              <AvatarItem
                key={idx}
                selected={guestAvatar === opt}
                onClick={() => setGuestAvatar(opt)}
                title={opt.startsWith('emoji:') ? opt.replace('emoji:', '') : '头像'}
              >
                {opt.startsWith('emoji:') ? (
                  <span className="emoji">{opt.replace('emoji:', '')}</span>
                ) : (
                  <img src={opt} alt="avatar" onError={(e) => { e.currentTarget.style.display = 'none'; }} />
                )}
              </AvatarItem>
            ))}
          </AvatarGrid>

          <SubmitButton type="button" disabled={loading} onClick={handleGuestLogin} style={{ marginTop: '16px' }}>
            {loading ? <LoadingSpinner /> : (<>
              <ImageIcon /> 以游客身份进入
            </>)}
          </SubmitButton>
        </GuestSection>
      </LoginCard>
    </LoginContainer>
  );
};

export default Login;
